import { Button, Form, PlayButton } from '@southfields-digital/mpxlive-components';
import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { StateType } from 'src/redux/reducers';

import useKeypress from 'src/hooks/useKeypress';
import {
  REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
  REQUEST_ANIMATE_IN_PREVIEW_GRAPHIC,
  REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
  REQUEST_ANIMATE_OUT_PREVIEW_GRAPHIC,
  REQUEST_UPDATE_LIVE_GRAPHIC,
} from 'src/redux/reducers/control';
import { REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB } from 'src/redux/reducers/playlistDisplayHub';

import styles from './DisplayHubControlForm.module.scss';

type Props = {
  selectedDisplayHub: PlaylistDisplayHub;
};

export default function DisplayHubControlForm({ selectedDisplayHub }: Props) {
  const dispatch = useDispatch();
  const [name, setName] = useState<string>('');
  const [editable, setEditable] = useState<boolean>(false);

  const { displayHubGraphics } = useSelector((state: StateType) => state.playlistGraphic);
  const { selectedPlaylistDisplayHubStory } = useSelector(
    (state: StateType) => state.playlistDisplayHubStory
  );

  const selectedStoryGraphics = useMemo(
    () =>
      displayHubGraphics?.filter((graphic) =>
        selectedPlaylistDisplayHubStory?.playlistGraphicIds.includes(graphic.id)
      ),
    [displayHubGraphics, selectedPlaylistDisplayHubStory]
  );

  const isAllLive = useMemo(
    () =>
      !!(
        selectedStoryGraphics?.length &&
        selectedStoryGraphics
          .filter((playlistGraphic) => !playlistGraphic.isConcept)
          .every((playlistGraphic) => playlistGraphic.status.live)
      ),
    [selectedStoryGraphics]
  );

  const isAllPreview = useMemo(
    () =>
      !!(
        selectedStoryGraphics?.length &&
        selectedStoryGraphics
          .filter((playlistGraphic) => !playlistGraphic.isConcept)
          .every((playlistGraphic) => playlistGraphic.status.preview)
      ),
    [selectedStoryGraphics]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(selectedDisplayHub.name);
  }, [selectedDisplayHub.name]);

  const handleSubmitForm = () => {
    updateProperties();
    setEditable(false);
  };

  const updateProperties = () =>
    dispatch({
      type: REQUEST_UPDATE_PLAYLIST_DISPLAY_HUB,
      payload: { displayHub: { ...selectedDisplayHub, name } },
    });

  const previewIn = () => {
    if (!selectedPlaylistDisplayHubStory) return;

    for (const playlistGraphicId of selectedPlaylistDisplayHubStory.playlistGraphicIds) {
      dispatch({
        type: REQUEST_ANIMATE_IN_PREVIEW_GRAPHIC,
        payload: { playlistGraphicId },
      });
    }
  };

  const previewOut = () => {
    if (!selectedPlaylistDisplayHubStory) return;

    for (const playlistGraphicId of selectedPlaylistDisplayHubStory.playlistGraphicIds) {
      dispatch({
        type: REQUEST_ANIMATE_OUT_PREVIEW_GRAPHIC,
        payload: { playlistGraphicId },
      });
    }
  };

  const handleTogglePreview = () => {
    if (!selectedDisplayHub) return;

    if (isAllPreview) previewOut();
    else previewIn();
  };

  const liveIn = () => {
    if (!selectedStoryGraphics) return;

    for (const selectedStoryGraphic of selectedStoryGraphics) {
      if (selectedStoryGraphic.status.live || selectedStoryGraphic.isConcept) continue;
      dispatch({
        type: REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
        payload: { playlistGraphicId: selectedStoryGraphic.id },
      });
    }
  };

  const liveOut = () => {
    if (!selectedPlaylistDisplayHubStory) return;

    for (const playlistGraphicId of selectedPlaylistDisplayHubStory.playlistGraphicIds) {
      dispatch({
        type: REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
        payload: { playlistGraphicId },
      });
    }
  };

  const handleToggleLive = () => {
    if (!selectedDisplayHub) return;

    if (isAllLive) {
      liveOut();
    } else {
      liveIn();
    }
  };

  const handleSwapItems = () => {
    if (!selectedPlaylistDisplayHubStory) return;

    for (const playlistGraphicId of selectedPlaylistDisplayHubStory.playlistGraphicIds) {
      dispatch({
        type: REQUEST_UPDATE_LIVE_GRAPHIC,
        payload: { playlistGraphicId, payload: 'swap:fade' },
      });
    }
  };

  useKeypress('F1', {}, liveIn);
  useKeypress('F2', {}, liveOut);
  useKeypress('F3', {}, previewIn);
  useKeypress('F4', {}, previewOut);

  const handleClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setEditable(true);
    inputRef.current?.focus();
  };

  const handleClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.detail === 2) {
      setEditable(true);
      inputRef.current?.focus();
    }
  };

  const handleBlurInput = () => {
    updateProperties();
    setEditable(false);
  };

  return (
    <div className={styles.Container}>
      <Form onSubmit={handleSubmitForm} className={styles.Form}>
        <Button
          as="button"
          variant="tertiary"
          iconProps={{ icon: 'pencil', size: 16, color: '#5B5D70' }}
          onClick={handleClickEdit}
          className={styles.EditButton}
        />
        <input
          name="name"
          ref={inputRef}
          readOnly={!editable}
          className={styles.Input}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.currentTarget.value)}
          onClick={handleClickInput}
          onBlur={handleBlurInput}
        />
      </Form>
      <div className={styles.Controls}>
        <div className={styles.PreviewControls}>
          <PlayButton
            hasIcon
            variant="preview"
            onClick={handleTogglePreview}
            isRunning={isAllPreview}
            className={styles.PreviewButton}
          >
            {isAllPreview ? 'Preview Out' : 'Preview'}
          </PlayButton>
        </div>
        <div className={styles.SwapControls}>
          <PlayButton
            hasIcon
            variant="fade"
            iconPosition="right"
            onClick={handleSwapItems}
            isRunning={false}
            disabled={!isAllLive}
            className={styles.SwapButton}
          >
            Swap
          </PlayButton>
        </div>
        <div className={styles.LiveControls}>
          <PlayButton
            hasIcon
            variant="live"
            onClick={handleToggleLive}
            isRunning={isAllLive}
            className={styles.LiveButton}
          >
            {isAllLive ? 'Live Out' : 'Live'}
          </PlayButton>
        </div>
      </div>
    </div>
  );
}
